import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { X, FileText, Download, UploadCloud, Check, HelpCircle, ArrowUpDown, CheckCircle } from "lucide-react";

// Field Options for Mapping
const STUDENT_FIELDS = [
    { value: "ignore", label: "🚫 무시하기" },
    { value: "name", label: "이름 (필수)" },
    { value: "phone", label: "연락처" },
    { value: "guardian1Name", label: "보호자1 관계" },
    { value: "guardian1Phone", label: "보호자1 연락처" },
    { value: "guardian2Name", label: "보호자2 관계" },
    { value: "guardian2Phone", label: "보호자2 연락처" },
    { value: "grade", label: "부서(학년)" },
    { value: "class", label: "반" },
    { value: "birth", label: "생년월일" },
    { value: "address", label: "주소" },
    { value: "school", label: "학교/직장" },
    { value: "faithState", label: "신앙상태" },
    { value: "gender", label: "성별" },
    { value: "notes", label: "비고" }
];

const TEACHER_FIELDS = [
    { value: "ignore", label: "🚫 무시하기" },
    { value: "name", label: "이름 (필수)" },
    { value: "phone", label: "연락처" },
    { value: "role", label: "직분 (교사/부장/전도사)" },
    { value: "charge", label: "담당 부서" },
    { value: "birth", label: "생년월일" },
    { value: "address", label: "주소" },
    { value: "school", label: "학교/직장" },
    { value: "faithState", label: "신앙상태" },
    { value: "gender", label: "성별" },
    { value: "notes", label: "비고" }
];

export default function BulkRegistrationModal({
    isOpen,
    onClose,
    type = 'student', // 'student' | 'teacher'
    onRegister, // (data: any[]) => void
    openAlert, // (title, msg) => void
    defaultDepartment = '' // [NEW] 사용자 기본 부서
}) {
    const [step, setStep] = useState(1);
    const [bulkText, setBulkText] = useState("");
    const [parsedRows, setParsedRows] = useState([]);
    const [columnMapping, setColumnMapping] = useState([]);

    // [NEW] Draggable Modal State
    const [modalPos, setModalPos] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (!isOpen) {
            setStep(1);
            setModalPos({ x: 0, y: 0 });
            setBulkText("");
            setParsedRows([]);
            setColumnMapping([]);
        }
    }, [isOpen]);

    const handleMouseDown = (e) => {
        if (e.target.closest('button') || e.target.closest('select')) return;
        setIsDragging(true);
        setDragStart({
            x: e.clientX - modalPos.x,
            y: e.clientY - modalPos.y
        });
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;
            setModalPos({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        };
        const handleMouseUp = () => setIsDragging(false);

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = 'none';
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = '';
        };
    }, [isDragging, dragStart]);

    const FIELD_OPTIONS = type === 'teacher' ? TEACHER_FIELDS : STUDENT_FIELDS;

    // 1. Template Download
    const downloadTemplate = () => {
        const isTeacher = type === 'teacher';
        const headers = isTeacher
            ? ["이름", "성별", "생년월일", "연락처", "학교/직장", "담당부서", "직분", "주소", "신앙상태", "비고"]
            : ["이름", "성별", "생년월일", "연락처", "보호자1 관계", "보호자1 연락처", "보호자2 관계", "보호자2 연락처", "학교/직장", "부서", "반", "주소", "신앙상태", "비고"];

        let data = [headers];

        if (isTeacher) {
            data.push(["김교사", "여", "1990-01-01", "010-1234-5678", "기쁨연구소", "고등부", "교사", "서울시 강남구", "기존신자", "찬양팀 인도"]);
        } else {
            data.push(["홍학생", "남", "2010-05-05", "010-9876-5432", "어머니", "010-1111-2222", "아버지", "010-3333-4444", "기쁨중학교", "중등부", "중등1반", "서울시 서초구", "양육중", "축구 좋아함"]);
        }

        const ws = XLSX.utils.aoa_to_sheet(data);
        const filename = type === 'teacher' ? "교사일괄등록_양식.xlsx" : "학생일괄등록_양식.xlsx";

        // [FIX] 컬럼 너비 설정
        if (isTeacher) {
            ws['!cols'] = [
                { wch: 10 },  // 이름
                { wch: 6 },   // 성별
                { wch: 12 },  // 생년월일
                { wch: 15 },  // 연락처
                { wch: 15 },  // 학교/직장
                { wch: 10 },  // 담당부서
                { wch: 10 },  // 직분
                { wch: 30 },  // 주소
                { wch: 10 },  // 신앙상태
                { wch: 25 },  // 비고
            ];
        } else {
            // 학생 양식 컬럼 너비
            ws['!cols'] = [
                { wch: 10 },  // 이름
                { wch: 6 },   // 성별
                { wch: 12 },  // 생년월일
                { wch: 15 },  // 연락처
                { wch: 12 },  // 보호자1 관계
                { wch: 15 },  // 보호자1 연락처
                { wch: 12 },  // 보호자2 관계
                { wch: 15 },  // 보호자2 연락처
                { wch: 15 },  // 학교/직장
                { wch: 10 },  // 부서
                { wch: 8 },   // 반
                { wch: 30 },  // 주소
                { wch: 10 },  // 신앙상태
                { wch: 25 },  // 비고
            ];
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "등록명단");
        XLSX.writeFile(wb, filename);
    };

    // 2. File Upload & Auto Mapping
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            const bstr = evt.target.result;
            const wb = XLSX.read(bstr, { type: "binary" });
            const wsname = wb.SheetNames[0];
            const ws = wb.Sheets[wsname];
            const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

            if (data.length < 2) {
                if (openAlert) openAlert("데이터 없음", "파일에 데이터가 없습니다.");
                else alert("데이터가 없습니다.");
                return;
            }

            const headers = data[0];
            const rows = data.slice(1).filter(r => r.length > 0);

            setParsedRows(rows);

            // Smart Mapping Logic
            const newMapping = headers.map(h => {
                const header = String(h || "").trim();
                if (header === "이름") return "name";
                if (header === "성별") return "gender";
                if (header === "생년월일") return "birth";
                if (header === "연락처") return "phone";
                if (header === "보호자1 관계" || header === "보호자 관계") return "guardian1Name";
                if (header === "보호자1 연락처" || header === "보호자 연락처") return "guardian1Phone";
                if (header === "보호자2 관계") return "guardian2Name";
                if (header === "보호자2 연락처") return "guardian2Phone";
                if (header === "학교/직장" || header === "학교" || header === "직장") return "school";
                if (header === "부서" || header === "학년" || header === "담당부서" || header === "소속") return type === 'teacher' ? "charge" : "grade";
                if (header === "반") return "class";
                if (header === "직분" || header === "역할") return "role";
                if (header === "주소") return "address";
                if (header === "신앙상태") return "faithState";
                if (header === "비고") return "notes";
                return "ignore";
            });

            setColumnMapping(newMapping);
            setStep(3); // Go to Preview
        };
        reader.readAsBinaryString(file);
    };

    // 3. Text Parse (Paste)
    // [FIX] 탭이 없는 줄은 이전 행의 마지막 셀에 합치기 (셀 내 줄바꿈 처리)
    const parseBulkText = () => {
        if (!bulkText.trim()) return;
        const rawLines = bulkText.trim().split('\n');
        const processedRows = [];

        rawLines.forEach(line => {
            const trimmedLine = line.trim();
            if (!trimmedLine) return; // 빈 줄 무시

            // 탭이 있으면 새로운 행으로 처리
            if (trimmedLine.includes('\t') || trimmedLine.includes(',')) {
                const cells = trimmedLine.split(/[\t,]+/).map(s => s.trim());
                processedRows.push(cells);
            } else {
                // 탭이 없으면 이전 행의 마지막 셀에 추가 (셀 내 줄바꿈)
                if (processedRows.length > 0) {
                    const lastRow = processedRows[processedRows.length - 1];
                    lastRow[lastRow.length - 1] += ' ' + trimmedLine;
                } else {
                    // 첫 줄인데 탭이 없으면 단일 셀 행으로 처리
                    processedRows.push([trimmedLine]);
                }
            }
        });

        setParsedRows(processedRows);
        const maxCols = Math.max(...processedRows.map(r => r.length));
        setColumnMapping(new Array(maxCols).fill("ignore"));
        setStep(3);
    };

    // 4. Submit
    const handleRegister = () => {
        const result = [];
        parsedRows.forEach((row) => {
            const entry = {};
            entry.gender = "남";
            if (type === 'teacher') {
                entry.role = "교사";
                entry.charge = defaultDepartment || "";
            } else {
                entry.grade = defaultDepartment || "";
                entry.faithState = "기존신자";
            }

            row.forEach((cell, colIdx) => {
                const field = columnMapping[colIdx];
                if (field && field !== "ignore") {
                    entry[field] = cell;
                }
            });

            if (entry.name) result.push(entry);
        });

        if (result.length === 0) {
            if (openAlert) openAlert("오류", "등록할 데이터가 없습니다.");
            else alert("등록할 데이터가 없습니다.");
            return;
        }

        onRegister(result);
        handleClose();
    };

    const handleClose = () => {
        setStep(1);
        onClose();
    };

    // ESC Key Handler
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") handleClose();
        };
        if (isOpen) window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen]);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 bg-transparent flex items-center justify-center z-[5000] p-4 pointer-events-none" style={{ zIndex: 5000 }} onClick={handleClose}>
            <div
                className="bg-white/95 backdrop-blur-xl rounded-[2.5rem] p-0 w-full max-w-4xl shadow-2xl relative border border-white/50 h-[85vh] flex flex-col overflow-hidden pointer-events-auto"
                onClick={e => e.stopPropagation()}
                style={{ transform: `translate(${modalPos.x}px, ${modalPos.y}px)` }}
            >
                {/* Draggable Header */}
                <div
                    className="flex justify-between items-center px-8 py-6 border-b border-gray-100/50 bg-white/50 shrink-0 cursor-move"
                    onMouseDown={handleMouseDown}
                >
                    <div>
                        <h3 className="text-3xl font-black text-[#2C3E30] flex items-center gap-3 font-do-hyeon">
                            <img src="https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Books.png" width={40} alt="icon" />
                            {type === 'teacher' ? '교사 일괄 등록' : '학생 일괄 등록'}
                        </h3>
                        <p className="text-gray-400 text-sm font-bold mt-1 ml-1">
                            {type === 'teacher' ? '교사 명단 엑셀을 업로드하여 한 번에 등록하세요.' : '학생 명단 엑셀을 업로드하여 한 번에 등록하세요.'}
                        </p>
                    </div>
                    <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition"><X size={24} /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                    {step === 1 ? (
                        <div className="flex-1 flex flex-col items-center justify-center gap-6 h-full min-h-[400px]">
                            {/* Template Download */}
                            <div className="w-full max-w-lg bg-gray-50 border border-gray-200 rounded-3xl p-6 flex items-center justify-between shadow-sm hover:shadow-md transition group">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm text-green-600 group-hover:scale-110 transition">
                                        <FileText size={24} />
                                    </div>
                                    <div className="text-left">
                                        <p className="font-bold text-gray-800">등록 양식 다운로드</p>
                                        <p className="text-xs text-gray-400">{type === 'teacher' ? '교사 등록용 엑셀 양식' : '학생 등록용 엑셀 양식'}</p>
                                    </div>
                                </div>
                                <button type="button" onClick={downloadTemplate} className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-xs font-bold hover:bg-green-50 hover:text-green-700 hover:border-green-200 transition flex items-center gap-1.5 shadow-sm">
                                    <Download size={14} /> 다운로드
                                </button>
                            </div>

                            {/* File Upload */}
                            <label className="w-full max-w-lg aspect-video bg-white border-2 border-dashed border-gray-300 rounded-[2.5rem] flex flex-col items-center justify-center gap-4 cursor-pointer hover:border-[#354F38] hover:bg-[#354F38]/5 transition group relative overflow-hidden">
                                <input type="file" className="hidden" accept=".xlsx, .xls, .csv" onChange={handleFileUpload} />
                                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center text-gray-300 group-hover:text-[#354F38] group-hover:bg-white group-hover:shadow-xl transition-all duration-300 z-10">
                                    <UploadCloud size={40} />
                                </div>
                                <div className="text-center z-10">
                                    <p className="font-black text-xl text-gray-400 group-hover:text-[#354F38] transition">엑셀 파일 업로드</p>
                                    <p className="text-xs text-gray-400 mt-1 mb-4">클릭하여 파일을 선택하거나 이곳으로 드래그하세요</p>
                                    <span className="px-3 py-1 bg-gray-100 text-gray-500 rounded-lg text-[10px] font-bold">.xlsx, .xls 지원</span>
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-tr from-[#354F38]/0 to-[#354F38]/0 group-hover:from-[#354F38]/5 group-hover:to-transparent transition-all duration-500"></div>
                            </label>

                            <div className="mt-4 flex items-center gap-4 w-full max-w-lg">
                                <div className="h-px bg-gray-200 flex-1"></div>
                                <span className="text-xs text-gray-400 font-bold">또는</span>
                                <div className="h-px bg-gray-200 flex-1"></div>
                            </div>

                            <button type="button" onClick={() => { setBulkText(""); setStep(2); }} className="text-xs text-gray-400 hover:text-gray-600 underline font-bold">
                                직접 텍스트로 붙여넣으시겠습니까?
                            </button>
                        </div>
                    ) : step === 2 ? (
                        <div className="flex-1 flex flex-col h-full min-h-[400px]">
                            <p className="text-sm text-gray-500 mb-4 font-medium">데이터를 복사하여 아래에 붙여넣으세요.</p>
                            <textarea
                                className="flex-1 w-full bg-[#F9F9F8] border border-gray-200 p-5 rounded-2xl font-mono text-sm focus:outline-none focus:border-[#354F38] transition resize-none mb-4 shadow-inner"
                                placeholder={`예시: \n홍길동\t${type === 'teacher' ? '010-1111-2222\t고등부\t교사' : '010-9876-5432\t중등부\t1반'}`}
                                value={bulkText}
                                onChange={(e) => setBulkText(e.target.value)}
                            />
                            <div className="flex gap-3">
                                <button type="button" onClick={() => setStep(1)} className="flex-1 py-4 bg-gray-100 text-gray-600 rounded-2xl font-bold hover:bg-gray-200 transition">취소</button>
                                <button type="button" onClick={parseBulkText} className="flex-[2] bg-[#354F38] text-white py-4 rounded-2xl font-black text-lg hover:bg-[#2C3E30] transition shadow-lg shadow-[#354F38]/20">다음 단계</button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex-1 flex flex-col overflow-hidden h-full">
                            <div className="flex items-center justify-between mb-4">
                                <p className="text-sm text-gray-500 font-bold">🔍 자동으로 인식된 필드를 확인하세요.</p>
                                <div className="flex gap-2">
                                    <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-bold border border-green-200 flex items-center gap-1">
                                        <CheckCircle size={12} /> {parsedRows.length}명 인식됨
                                    </span>
                                </div>
                            </div>

                            <div className="flex-1 overflow-auto border border-gray-200 rounded-2xl mb-6 custom-scrollbar bg-white">
                                <table className="w-full text-sm text-left relative">
                                    <thead className="bg-[#F9F9F8] sticky top-0 z-10 shadow-sm">
                                        <tr>
                                            {columnMapping.map((mapType, colIndex) => (
                                                <th key={colIndex} className="p-3 border-b border-r min-w-[140px]">
                                                    <div className="flex flex-col gap-2">
                                                        <span className="text-[10px] text-gray-400 font-bold uppercase ml-1">열 {colIndex + 1}</span>
                                                        <select
                                                            value={mapType}
                                                            onChange={(e) => {
                                                                const newMapping = [...columnMapping];
                                                                newMapping[colIndex] = e.target.value;
                                                                setColumnMapping(newMapping);
                                                            }}
                                                            className={`w-full p-2.5 rounded-xl text-sm font-bold border-2 outline-none transition cursor-pointer ${mapType === "ignore" ? "border-gray-200 text-gray-400 bg-white hover:border-gray-300" : "border-[#354F38] text-[#354F38] bg-[#354F38]/5"}`}
                                                        >
                                                            {FIELD_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                                                        </select>
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {parsedRows.slice(0, 50).map((row, rowIdx) => (
                                            <tr key={rowIdx} className="hover:bg-gray-50 transition border-b border-gray-50 last:border-0 group">
                                                {row.map((cell, cellIdx) => (
                                                    <td key={cellIdx} className={`p-4 border-r border-gray-50 truncate max-w-[150px] ${columnMapping[cellIdx] !== "ignore" ? "font-bold text-gray-700" : "text-gray-300"}`}>{cell || "-"}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {parsedRows.length > 50 && <div className="p-4 text-center text-gray-400 text-xs font-bold bg-gray-50">...외 {parsedRows.length - 50}명 더 있음</div>}
                            </div>

                            <div className="flex gap-3">
                                <button type="button" onClick={() => setStep(1)} className="flex-1 py-4 bg-gray-100 text-gray-600 rounded-2xl font-bold hover:bg-gray-200 transition flex items-center justify-center gap-2">
                                    <ArrowUpDown className="rotate-90" size={16} /> 다시 올리기
                                </button>
                                <button type="button" onClick={handleRegister} className="flex-[2] bg-[#354F38] text-white py-4 rounded-2xl font-black text-lg hover:bg-[#2C3E30] transition shadow-lg shadow-[#354F38]/20 flex items-center justify-center gap-2">
                                    <CheckCircle size={20} /> 총 {parsedRows.length}명 등록하기
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
